import { apiDelete, apiGet, apiPost } from '@/api/http';
import dayjs from '@/utils/dayjs';

export type JobRef = {
  id: string;
  name: string;
  location?: string;
  url?: string;
};

export type StageRef = {
  id: string;
  name: string;
  order: number;
  is_required?: boolean;
  is_interview?: boolean;
};

type WithStages = {
  stages: StageRef[];
};

export type AtsStatus = {
  state: 'NOT_CONNECTED' | 'AWAITING_INITIAL_SYNC' | 'CONNECTED';
  tool_name: string;
  last_sync_date?: Date;
  ats_jobs: JobRef[];
  job_connections: {
    ats_job: JobRef;
    local_job?: JobRef;
  }[];
  new_interview_stages: boolean;
};

export type AtsJobStatus = {
  local_job: JobRef & WithStages;
  ats_job?: JobRef & WithStages;
  stage_mappings: {
    ats_stage: StageRef;
    local_stage?: StageRef;
  }[];
};

export const getConnectLink = async (options?: {
  environment?: string;
  tool?: string;
  hostname?: string;
}) => {
  const data = await apiPost<{ link: string }>(
    `${import.meta.env.VITE_API_URL}integrations/ats/connection/link/`,
    { options },
  );
  return data.link;
};

export const getSyncStatus = () =>
  apiGet<{ awaiting_initial_sync: boolean; needs_reauth: boolean }>(
    `${import.meta.env.VITE_API_URL}integrations/ats/connection/sync-status/`,
  );

export const activateConnection = (token: string) =>
  apiPost<{ success?: boolean }>(
    `${import.meta.env.VITE_API_URL}integrations/ats/connection/activate/`,
    { token },
  );

export const dismissNewStages = () =>
  apiPost<{ success?: boolean }>(
    `${import.meta.env.VITE_API_URL}integrations/ats/connection/dismiss-new-stages/`,
  );

export const forceSync = () =>
  apiPost(`${import.meta.env.VITE_API_URL}integrations/ats/sync/`);

type GetAtsStatusQuery = {
  post_url?: string;
  remote_ids?: string;
  job_codes?: string;
  name_contains?: string;
  statuses?: string;
};

/** Job filters the connection endpoint accepts (mirrors the backend allowlist). */
const ATS_STATUS_FILTER_KEYS = [
  'post_url',
  'remote_ids',
  'job_codes',
  'name_contains',
  'statuses',
] as const;

/**
 * Narrow arbitrary params (often raw URL search params) to the connection
 * filters. Router/UI keys such as `sort`, `lang` or `token` must never reach
 * the API or the React Query key, or every URL change refetches the status.
 */
export const pickAtsStatusQuery = (
  params: Record<string, string | undefined>,
): Record<string, string> => {
  const query: Record<string, string> = {};
  ATS_STATUS_FILTER_KEYS.forEach((key) => {
    const value = params[key];
    if (value) {
      query[key] = value;
    }
  });
  return query;
};

export const ATS_JOB_FILTER_OPEN_JOBS: GetAtsStatusQuery = {
  statuses: 'OPEN',
};

export const getAtsStatus = async (jobFilters: GetAtsStatusQuery = {}) => {
  const queryString = new URLSearchParams(jobFilters).toString();
  const data = await apiGet<AtsStatus>(
    `${import.meta.env.VITE_API_URL}integrations/ats/connection/?${queryString}`,
  );
  const result: AtsStatus = {
    ...data,
    last_sync_date: data.last_sync_date
      ? dayjs(data.last_sync_date).toDate()
      : data.last_sync_date,
  };
  return result;
};

/**
 * Connects an existing HirePort job to an ATS job, with no stage mappings
 * @param localJobId the id of the job in HirePort
 * @param remoteJobId the id of the job in the ATS broker
 */
export const connectJob = (localJobId: string, remoteJobId: string) =>
  apiPost(`${import.meta.env.VITE_API_URL}integrations/ats/jobs/`, {
    local_job: { id: localJobId },
    ats_job: { id: remoteJobId },
    stage_mappings: [],
  });

/**
 * Creates a new HirePort job which is connected to an ATS job,
 * and is created with automatic maximal stage mappings
 * @param remoteJobId the ide of the job in the ATS broker to connect to
 */
export const createConnectedJob = (remoteJobId: string) =>
  apiPost(`${import.meta.env.VITE_API_URL}integrations/ats/jobs/`, {
    ats_job: { id: remoteJobId },
  });

export const getAtsJobStatus = (jobId: string) =>
  apiGet<AtsJobStatus>(
    `${import.meta.env.VITE_API_URL}integrations/ats/jobs/${jobId}/`,
  );

export const createStageMappings = (
  jobId: string,
  mappings: { localId: string; atsId: string }[],
) =>
  apiPost(
    `${import.meta.env.VITE_API_URL}integrations/ats/jobs/${jobId}/stages/`,
    mappings.map(({ localId, atsId }) => ({
      local_stage: { id: localId },
      ats_stage: { id: atsId },
    })),
  );

export const disconnectJob = (jobId: string) =>
  apiDelete(`${import.meta.env.VITE_API_URL}integrations/ats/jobs/${jobId}/`);

export const disconnectAts = () =>
  apiDelete(`${import.meta.env.VITE_API_URL}integrations/ats/connection/`);

export type DefaultStageMappingType =
  | 'INTRODUCED'
  | 'INTERVIEW'
  | 'OFFERED'
  | 'HIRED'
  | 'REJECTED'
  | 'ON_HOLD'
  | 'WITHDRAWN';

export interface DefaultStageMapping {
  id?: string;
  provider: string;
  provider_name?: string;
  company_name?: string;
  external_name: string;
  external_stage_name: string;
  stage_type: DefaultStageMappingType | null;
  order: number;
  is_unmapped: boolean;
  /** True when left in Unmapped — jobs skip it; provider API surfaces it as unmapped. */
  is_new?: boolean;
  ai_suggestion?: StageMappingSuggestion;
}

export const getDefaultStageMappings = () =>
  apiGet<DefaultStageMapping[]>(
    `${import.meta.env.VITE_API_URL}integrations/ats/default-mappings/provider/`,
  );

export interface CandidateToReevaluate {
  candidate_id: string;
  candidate_name: string;
  job_id: string;
  job_name: string;
  ats_stage_name: string;
  current_stage_name: string;
  current_stage_order: number;
  new_stage_name: string;
  new_stage_order: number;
  new_stage_type: string;
}

export interface DryRunResult {
  is_safe: boolean;
  summary: {
    mappings_added: number;
    mappings_removed: number;
    mappings_unmapped: number;
    mappings_pending?: number;
    mappings_type_changed: number;
    mappings_reordered: number;
    mappings_unchanged: number;
    total_changes: number;
  };
  jobs_count: number;
  jobs_by_company: {
    [companyId: string]: {
      company_name: string;
      job_count: number;
      jobs: Array<{
        id: string;
        title: string;
      }>;
    };
  };
  candidates_count: number;
  candidates_to_reevaluate: CandidateToReevaluate[];
  affected_companies: Array<{
    id: string;
    name: string;
    is_super_company: boolean;
  }>;
  mapping_changes: {
    added: Array<{
      ats_id: string;
      ats_name: string;
      stage_type: string;
      order: number;
    }>;
    removed: Array<{
      ats_id: string;
      ats_name: string;
      old_type: string;
      old_order: number;
    }>;
    unmapped: Array<{
      ats_id: string;
      ats_name: string;
      old_type: string;
      old_order: number;
    }>;
    pending?: Array<{
      ats_id: string;
      ats_name: string;
      old_type: string;
      old_order: number;
    }>;
    type_changed: Array<{
      ats_id: string;
      ats_name: string;
      old_type: string;
      new_type: string;
      order: number;
    }>;
    reordered: Array<{
      ats_id: string;
      ats_name: string;
      stage_type: string;
      old_order: number;
      new_order: number;
    }>;
  };
  candidates_moved_to_fallback?: {
    count: number;
    candidates: Array<{
      candidate_id: string;
      candidate_name: string;
      job_id?: string;
      job_name: string;
      stage_name: string;
      ats_stage_name?: string;
      ats_stage_id?: string;
    }>;
    unmapped_stages: string[];
    info_message: string | null;
  };
}

export const dryRunDefaultStageMappings = async (
  mappings: Partial<DefaultStageMapping>[],
): Promise<DryRunResult> => {
  // Hidden and Unmapped stages are sent without a stage_type
  const processedMappings = mappings.map((mapping) => ({
    ...mapping,
    order: mapping.order ?? 0,
  }));

  return apiPost<DryRunResult>(
    `${import.meta.env.VITE_API_URL}integrations/ats/default-mappings/dry-run/`,
    processedMappings,
  );
};

export const bulkUpdateDefaultStageMappings = (
  mappings: Partial<DefaultStageMapping>[],
) => {
  // Hidden and Unmapped stages are sent without a stage_type
  const processedMappings = mappings.map((mapping) => ({
    ...mapping,
    order: mapping.order ?? 0,
  }));

  return apiPost<DefaultStageMapping[]>(
    `${import.meta.env.VITE_API_URL}integrations/ats/default-mappings/bulk-update/`,
    processedMappings,
  );
};

export type ApplyDefaultStageMappingsResult = {
  updated_jobs_count: number;
};

export const applyDefaultStageMappingsToAllJobs = () =>
  apiPost<ApplyDefaultStageMappingsResult>(
    `${import.meta.env.VITE_API_URL}integrations/ats/default-mappings/apply-to-all-jobs/`,
  );

export type SyncApplicationStagesResult = {
  new_stages_count: number;
};

export const syncApplicationStages = () =>
  apiPost<SyncApplicationStagesResult>(
    `${import.meta.env.VITE_API_URL}integrations/ats/default-mappings/sync/`,
  );

export interface HireportStage {
  type: DefaultStageMappingType;
  description: string;
}

export interface UnmappedStage {
  id: string;
  name: string;
}

export interface ExistingStageMapping {
  name: string;
  stage_type: DefaultStageMappingType | null;
  is_unmapped: boolean;
}

export interface StageMappingSuggestion {
  external_stage_id: string;
  external_stage_name: string;
  suggested_stage_type: DefaultStageMappingType | 'HIDDEN';
  reasoning?: string;
  /** Position among the suggested Interview stages, in hiring-process order */
  order?: number | null;
}

export interface AISuggestionsResponse {
  suggestions: StageMappingSuggestion[];
  available: boolean;
  /** Why suggestions are unavailable: no API key configured, or the AI call failed */
  reason?: 'no_api_key' | 'ai_error';
  message?: string;
}

export const getAIStageMappingSuggestions = async (
  hireportStages: HireportStage[],
  unmappedStages: UnmappedStage[],
  existingMappings: ExistingStageMapping[] = [],
): Promise<AISuggestionsResponse> =>
  apiPost<AISuggestionsResponse>(
    `${import.meta.env.VITE_API_URL}integrations/ats/default-mappings/ai-suggestions/`,
    {
      hireport_stages: hireportStages,
      unmapped_stages: unmappedStages,
      existing_mappings: existingMappings,
    },
  );
